import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { v4 as uuidv4 } from 'uuid';

import { reportEvent } from '~/domains/analytics';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { useWalletRefundableBreakdown } from '~/domains/payments/hooks/useWalletRefundableBreakdown';
import { invalidateDepositBonuses } from '~/domains/payments/deposits/utils/query-invalidation';
import { depositToWallet } from '~/services/wallet/wallet.service.api';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';
import { PaymentType } from '~/services/wallet/interfaces/wallets.interface';
import { getErrorTranslations } from '~/domains/payments/utils/utils';
import { roundToCents } from '~/domains/payments/utils/currencyRounding';

import { isCustomDeposit } from './consts';

export function useDepositWithSavedCard({
  setCompletedDepositAmount,
}: {
  setCompletedDepositAmount?: (amount: number, response?: IPostWalletsDepositsResponse) => void;
}) {
  const { t } = useTranslation('payments');
  const queryClient = useQueryClient();
  const { refetch: refetchWalletBalance } = useWalletBalance();
  const { refetch: refetchRefundableBreakdown } = useWalletRefundableBreakdown();

  return useMutation({
    mutationFn: async ({
      depositAmountWithFees,
      merchantRefNum = uuidv4(),
      cvv,
      paymentHandleId,
      bonusId,
    }: {
      depositAmount: number;
      depositAmountWithFees: number;
      merchantRefNum?: string;
      cvv: string;
      paymentHandleId: string;
      bonusId?: string;
    }) =>
      depositToWallet({
        amount: roundToCents(depositAmountWithFees),
        merchantRefNum,
        cvv,
        paymentHandleId,
        currency: 'USD',
        paymentType: PaymentType.CARD,
        ...(bonusId ? { bonusId } : {}),
      }),
    onSuccess: (response, payload) => {
      if (response.data.status === 'COMPLETED') {
        reportEvent('Transactions > Deposit', {
          deposit_id: response.id,
          deposit_method: 'Card (saved)',
          deposit_amount: payload.depositAmount,
          is_custom_deposit: isCustomDeposit(payload.depositAmount),
          currency: 'USD',
          bonus_id: payload.bonusId,
        });
        refetchWalletBalance();
        refetchRefundableBreakdown();
        invalidateDepositBonuses(queryClient, payload.bonusId);
        setCompletedDepositAmount?.(payload.depositAmount, response);
      } else {
        showNotification({
          // eslint-disable @typescript-eslint/ban-ts-comment
          // @ts-expect-error Missing types for error response in Swagger
          title: response.error,
          // @ts-expect-error Missing types for error response in Swagger
          message: response?.data?.message || response.message,
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage = getErrorTranslations(t, error.code);

      if (error.code === 'ERR_BAD_REQUEST') {
        errorMessage.title = t('deposits.errors.savedCard');
        errorMessage.message = error.response?.data?.message ?? '';
      }

      showNotification({
        title: errorMessage.title,
        message: errorMessage.message,
        color: 'red',
        autoClose: 5000,
      });
    },
  });
}
